import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Search, SlidersHorizontal } from "lucide-react";

const CATEGORIES = [
  { slug: "", name: "All" },
  { slug: "notebooks", name: "Notebooks" },
  { slug: "writing", name: "Writing" },
  { slug: "art", name: "Art" },
  { slug: "backpacks", name: "Backpacks" },
  { slug: "classroom", name: "Classroom" },
  { slug: "tech", name: "Tech" },
];

export const Shop = () => {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const category = params.get("category") || "";
  const q = params.get("q") || "";
  const [search, setSearch] = useState(q);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (category) p.category = category;
    if (q) p.q = q;
    api.get("/products", { params: p }).then(({ data }) => setProducts(data)).finally(() => setLoading(false));
  }, [category, q]);

  const setCategory = (slug) => {
    const p = new URLSearchParams(params);
    if (slug) p.set("category", slug); else p.delete("category");
    setParams(p);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (search) p.set("q", search); else p.delete("q");
    setParams(p);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-indigo-600 mb-2">Shop</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold">All school supplies</h1>
        </div>
        <form onSubmit={submitSearch} className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…" className="pl-9 rounded-full" data-testid="shop-search-input" />
        </form>
      </div>

      <div className="flex gap-2 flex-wrap mb-8" data-testid="shop-category-filters">
        {CATEGORIES.map((c) => (
          <Button key={c.slug || "all"} size="sm"
            variant={category === c.slug ? "default" : "outline"}
            className={`rounded-full ${category === c.slug ? "bg-indigo-600 hover:bg-indigo-700" : "border-slate-300"}`}
            onClick={() => setCategory(c.slug)}
            data-testid={`filter-${c.slug || "all"}`}
          >
            {c.name}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {[...Array(8)].map((_, i) => <div key={i} className="bg-slate-100 rounded-2xl aspect-[3/4] animate-pulse" />)}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-24 bg-white rounded-2xl border border-slate-200">
          <SlidersHorizontal className="w-8 h-8 mx-auto text-slate-400" />
          <div className="mt-3 font-semibold">No products found</div>
          <div className="text-sm text-slate-500">Try a different category or search term.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
        </div>
      )}
    </div>
  );
};
